import { useEffect, useState } from "react";

// Tri-state theme picker: "system" follows prefers-color-scheme, "light"
// and "dark" override. Persisted to localStorage so the choice survives
// reloads. We toggle through system → dark → light to keep the button a
// single click rather than a dropdown.
type Theme = "system" | "light" | "dark";

const STORAGE_KEY = "asktogether.theme";

function readStored(): Theme {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" ? v : "system";
}

function applyTheme(t: Theme) {
  const root = document.documentElement;
  if (t === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", t);
  }
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => readStored());

  useEffect(() => {
    applyTheme(theme);
    if (theme === "system") {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, theme);
    }
  }, [theme]);

  function next() {
    setTheme((prev) =>
      prev === "system" ? "dark" : prev === "dark" ? "light" : "system",
    );
  }

  const label =
    theme === "system" ? "🖥 Auto" : theme === "dark" ? "🌙 Dark" : "☀️ Light";
  const aria =
    theme === "system"
      ? "Theme: follows system. Click to switch to dark."
      : theme === "dark"
      ? "Theme: dark. Click to switch to light."
      : "Theme: light. Click to follow system.";

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={next}
      aria-label={aria}
      title={aria}
    >
      {label}
    </button>
  );
}
